import { type JSX, useEffect, useRef, useState } from 'react';

import type { FeatureFlag } from '@schema/features.js';
import type { StatusBar as StatusBarConfig } from '@schema/layout.js';

import { Clock } from '../Clock.js';
import { useDexDetector } from '../use-dex-detector.js';
import { usePenDetector } from '../use-pen-detector.js';

export interface LauncherHeaderProps {
  /** Title shown next to the brand mark — typically the active profile name or deployment name. */
  title: string;
  /** Sub-line of metadata shown in the menu dropdown (device serial or
   *  similar). */
  deviceLabel?: string;
  /** Open the diagnostic / device-info modal. Wired to Pro's diagnostic overlay. */
  onOpenDeviceInfo?: () => void;
  /** Open the tools modal. Placeholder for Phase 4.x. */
  onOpenTools?: () => void;
  /**
   * Reload the launcher (re-pull config on the hosted-URL path). When
   * set, a "Refresh now" item appears at the top of the menu. Omitted
   * in the Builder preview so the iframe doesn't offer a self-reload.
   */
  onRefresh?: () => void;
  /**
   * Number of unread broadcasts to badge on the bell. When undefined the
   * bell is hidden — Core / Free tier devices and the demo don't have
   * the `broadcasts` entitlement and shouldn't see the icon at all.
   */
  unreadBroadcasts?: number;
  /** Click handler for the bell. Required when `unreadBroadcasts` is set. */
  onOpenBroadcasts?: () => void;
  /**
   * Status-bar config from the active profile. Drives `visible` (hide
   * the whole bar) and the per-icon toggles (clock / battery / signal /
   * current user). Passing this is optional; when omitted we render the
   * legacy "always visible, no extras" layout.
   */
  statusBar?: StatusBarConfig;
  /**
   * Entitled features — gates the `showCurrentUser` pill on the
   * `ssoDisplay` (Enterprise) flag.
   */
  features?: ReadonlySet<FeatureFlag>;
  /**
   * Resolved signed-in user name for the SSO pill (from the agent host's
   * getCurrentUser). When the pill is shown but this is undefined — no
   * host capability, logged out, or still resolving — we fall back to a
   * neutral label rather than an empty pill.
   */
  currentUser?: string;
  /**
   * Logo image source — either a data URL (Portal-uploaded inline) or
   * a resolved asset URL. When set, replaces the default briefcase
   * brand icon. Caller is responsible for gating on Pro `theming`.
   */
  logoSrc?: string;
  /**
   * Battery charge level (0..1) from the agent host. Rendered as a pill
   * only when `statusBar.showBattery` is on AND a real value is present —
   * browser/demo hosts leave it undefined and the indicator stays hidden.
   */
  batteryLevel?: number;
  /** Whether the device is charging; decorates the battery pill. */
  batteryCharging?: boolean;
  /**
   * Signal strength in dBm from the agent host. Rendered (as bars) only
   * when `statusBar.showSignal` is on AND a real value is present.
   */
  signalDbm?: number;
  /**
   * Current effective theme mode shown in the menu's "Theme" item. When
   * omitted the menu item is hidden — Core / Free devices and any other
   * non-Pro-theming context shouldn't see the switcher. Parent gates on
   * the `theming` flag.
   */
  themeMode?: 'auto' | 'always' | 'never';
  /**
   * Click handler for the Theme menu item. Required when `themeMode` is
   * set. Convention is to cycle auto → always → never on each tap.
   */
  onCycleThemeMode?: () => void;
}

/**
 * Top app bar — brand on the left, bell + menu on the right. Mirrors the
 * navy "Operations" header from the existing product screenshots.
 *
 * The menu dropdown shows the device serial/label and surfaces the two
 * canonical service entry points: Device Info and Tools. Both are no-ops
 * here (the renderer doesn't own those modals); the parent wires real
 * handlers from the engine + Pro features.
 */
export function LauncherHeader({
  title,
  deviceLabel,
  onOpenDeviceInfo,
  onOpenTools,
  onRefresh,
  unreadBroadcasts,
  onOpenBroadcasts,
  statusBar,
  features,
  currentUser,
  logoSrc,
  batteryLevel,
  batteryCharging,
  signalDbm,
  themeMode,
  onCycleThemeMode,
}: LauncherHeaderProps): JSX.Element | null {
  // statusBar.visible: false → hide the entire header. Falsy `statusBar`
  // (undefined) is treated as "visible" so callers that don't thread
  // the config through still see the legacy behavior.
  if (statusBar !== undefined && statusBar.visible === false) return null;

  const showClock = statusBar?.showClock === true;
  const showSso =
    statusBar?.showCurrentUser === true && features?.has('ssoDisplay') === true;
  // Battery / signal: the WebView can't read these itself (Web Battery API
  // is removed, no web signal API), so we render them ONLY from real values
  // the agent host supplies (parent passes via useDeviceStatus). When the
  // host can't supply them — browser/demo, or no MDM — the value is
  // undefined and we render nothing rather than a misleading stub.
  const showBattery = statusBar?.showBattery === true && batteryLevel !== undefined;
  const batteryPct = batteryLevel !== undefined ? Math.round(batteryLevel * 100) : 0;
  const showSignal = statusBar?.showSignal === true && signalDbm !== undefined;

  const showBell = unreadBroadcasts !== undefined;
  const badge = unreadBroadcasts ?? 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape so the dropdown behaves like the screenshots.
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onPointer(e: PointerEvent): void {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // S Pen indicator — surfaces a small ✏️ in the header when a stylus
  // is active AND the Samsung OEM module is loaded. Hides on devices
  // without `oem.samsung` in the effective feature set, even if the
  // pointer detector picks up a stylus, so we don't over-claim Samsung-
  // specific behaviour on non-Samsung Android tablets. Real customer
  // value: signals "annotation / signature affordances are live" to
  // operators handing off a tablet mid-shift.
  const penActive = usePenDetector();
  const showPen = penActive && features?.has('oem.samsung') === true;

  // DeX / desktop-mode indicator — feature-gated detection picks the
  // best available signal (native bridge on Enterprise, JS heuristic
  // on Pro). Pill flips on whenever the host is in a desktop posture.
  const dex = useDexDetector(features ?? new Set());
  const showDex = dex.active;

  return (
    <header className="launcher-header" data-testid="launcher-header">
      <div className="launcher-header__brand">
        {logoSrc !== undefined ? (
          <img
            className="launcher-header__brand-logo"
            src={logoSrc}
            alt=""
            data-testid="launcher-header-logo"
          />
        ) : (
          <GridLogo className="launcher-header__brand-icon" />
        )}
        <span className="launcher-header__title">{title}</span>
      </div>
      <div className="launcher-header__actions" ref={menuRef}>
        {showClock && (
          <span className="launcher-header__status" data-testid="launcher-header-clock">
            <Clock />
          </span>
        )}
        {showSignal && signalDbm !== undefined && (
          <span
            className="launcher-header__status launcher-header__status--signal"
            aria-label={`signal ${String(signalDbm)} dBm`}
            title={`Signal: ${String(signalDbm)} dBm`}
            data-testid="launcher-header-signal"
          >
            <SignalIcon bars={signalBars(signalDbm)} />
          </span>
        )}
        {showBattery && (
          <span
            className="launcher-header__status launcher-header__status--battery"
            aria-label={`battery ${String(batteryPct)} percent${
              batteryCharging === true ? ', charging' : ''
            }`}
            title={`Battery: ${String(batteryPct)}%${batteryCharging === true ? ' (charging)' : ''}`}
            data-testid="launcher-header-battery"
          >
            <BatteryIcon level={batteryPct / 100} charging={batteryCharging === true} />
            {batteryPct}%
          </span>
        )}
        {showSso && (
          <span
            className="launcher-header__status launcher-header__status--user"
            aria-label="current user"
            data-testid="launcher-header-user"
          >
            {currentUser ?? 'User'}
          </span>
        )}
        {showPen && (
          <span
            className="launcher-header__status launcher-header__status--pen"
            aria-label="stylus active"
            title="S Pen active"
            data-testid="launcher-header-pen"
          >
            <PenIcon />
          </span>
        )}
        {showDex && (
          <span
            className="launcher-header__status launcher-header__status--dex"
            aria-label="desktop mode active"
            title={
              dex.source === 'native'
                ? 'DeX (native detection)'
                : 'Desktop mode (heuristic)'
            }
            data-testid="launcher-header-dex"
          >
            <DexIcon />
          </span>
        )}
        {showBell && (
          <button
            type="button"
            className="launcher-header__icon-button launcher-header__bell"
            aria-label={
              badge > 0
                ? `Notifications, ${String(badge)} unread`
                : 'Notifications'
            }
            onClick={onOpenBroadcasts}
            data-testid="launcher-header-bell"
          >
            <BellIcon />
            {badge > 0 && (
              <span className="launcher-header__badge" aria-hidden>
                {badge > 9 ? '9+' : String(badge)}
              </span>
            )}
          </button>
        )}
        <button
          type="button"
          className="launcher-header__icon-button"
          aria-label="Menu"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="launcher-header-menu"
        >
          <GridIcon />
        </button>
        {menuOpen && (
          <div className="launcher-header__menu" role="menu" data-testid="launcher-header-menu-popup">
            {deviceLabel !== undefined && (
              <div className="launcher-header__menu-label">{deviceLabel}</div>
            )}
            <button
              type="button"
              className="launcher-header__menu-item"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onOpenDeviceInfo?.();
              }}
            >
              <InfoIcon /> Device Info
            </button>
            <button
              type="button"
              className="launcher-header__menu-item"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onOpenTools?.();
              }}
            >
              <WrenchIcon /> Tools
            </button>
            {themeMode !== undefined && (
              <button
                type="button"
                className="launcher-header__menu-item"
                role="menuitem"
                onClick={() => {
                  // Keep the menu open so customers can see the label
                  // flip and tap again to land on their preferred mode.
                  onCycleThemeMode?.();
                }}
                data-testid="launcher-header-theme"
              >
                <ThemeIcon mode={themeMode} /> Theme: {THEME_MODE_LABEL[themeMode]}
              </button>
            )}
            {onRefresh !== undefined && (
              <button
                type="button"
                className="launcher-header__menu-item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onRefresh();
                }}
                data-testid="launcher-header-refresh"
              >
                <RefreshIcon /> Refresh now
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

/* ────────────── tiny inline SVG icons (matches screenshot weights) ────────────── */

/**
 * Default brand mark — a 3×3 grid of small squares that echoes the
 * ▦ glyph used on mobilauncher.com's marketing header. When the tenant
 * uploads a `theme.logoRef` (Pro), this is replaced by their logo;
 * otherwise this is the out-of-box launcher identity. currentColor so
 * the brand-icon CSS can tint it (defaults to --ml-accent → teal on
 * navy chrome).
 */
function GridLogo({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="2"  y="2"  width="5.333" height="5.333" rx="1" />
      <rect x="9.333" y="2" width="5.333" height="5.333" rx="1" />
      <rect x="16.667" y="2" width="5.333" height="5.333" rx="1" />
      <rect x="2"  y="9.333"  width="5.333" height="5.333" rx="1" />
      <rect x="9.333" y="9.333" width="5.333" height="5.333" rx="1" />
      <rect x="16.667" y="9.333" width="5.333" height="5.333" rx="1" />
      <rect x="2"  y="16.667" width="5.333" height="5.333" rx="1" />
      <rect x="9.333" y="16.667" width="5.333" height="5.333" rx="1" />
      <rect x="16.667" y="16.667" width="5.333" height="5.333" rx="1" />
    </svg>
  );
}
function BellIcon(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9Z" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  );
}
function GridIcon(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}
function InfoIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M11 10h2v7h-2v-7Zm0-3h2v2h-2V7Z" fill="white" />
    </svg>
  );
}
function RefreshIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}
function WrenchIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m14 6 4 4-7 7-4 1 1-4 6-8Z" />
      <path d="m6 16 2 2" />
    </svg>
  );
}
function PenIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 19l7-7 3 3-7 7-3-3z" />
      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
      <path d="M2 2l7.586 7.586" />
      <circle cx="11" cy="11" r="2" />
    </svg>
  );
}
/** Bucket a dBm reading into 0–4 bars for the signal glyph. */
function signalBars(dbm: number): number {
  if (dbm >= -60) return 4;
  if (dbm >= -70) return 3;
  if (dbm >= -80) return 2;
  if (dbm >= -95) return 1;
  return 0;
}

/** Four ascending bars; bars above `bars` render dimmed. */
function SignalIcon({ bars }: { bars: number }): JSX.Element {
  const heights = [6, 10, 14, 18];
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      {heights.map((h, i) => (
        <rect
          key={h}
          x={2 + i * 6}
          y={22 - h}
          width="4"
          height={h}
          rx="1"
          opacity={i < bars ? 1 : 0.25}
        />
      ))}
    </svg>
  );
}

/** Battery outline with a proportional fill; adds a bolt when charging. */
function BatteryIcon({ level, charging }: { level: number; charging: boolean }): JSX.Element {
  const clamped = Math.max(0, Math.min(1, level));
  const fillWidth = 16 * clamped;
  return (
    <svg width="22" height="18" viewBox="0 0 26 18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <rect x="1" y="3" width="20" height="12" rx="2" />
      <rect x="22" y="6" width="2.5" height="6" rx="1" fill="currentColor" stroke="none" />
      <rect x="3" y="5" width={fillWidth} height="8" rx="1" fill="currentColor" stroke="none" />
      {charging && (
        <path d="M12 4l-3 6h3l-1 5 4-7h-3l1-4z" fill="#fff" stroke="none" />
      )}
    </svg>
  );
}

function DexIcon(): JSX.Element {
  // Compact monitor + stand silhouette — reads as "desktop mode" at
  // 16px in the header pill. Stroke-only so the accent color flows
  // through `currentColor`.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

const THEME_MODE_LABEL = {
  auto: 'Auto',
  always: 'Dark',
  never: 'Light',
} as const;

/**
 * Sun / moon / half-moon glyphs for the three theme-mode states. Each
 * keeps the same 18px footprint as the other menu icons so the menu
 * column doesn't shift width as the user cycles.
 */
function ThemeIcon({ mode }: { mode: 'auto' | 'always' | 'never' }): JSX.Element {
  if (mode === 'always') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M21 13a8.5 8.5 0 0 1-11.4-11A1 1 0 0 0 8.4 1 10.5 10.5 0 1 0 23 16.6a1 1 0 0 0-2-.6Z" />
      </svg>
    );
  }
  if (mode === 'never') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  // 'auto' — half-moon / half-sun composite
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" stroke="none" />
    </svg>
  );
}
